import memoize from 'lodash/memoize';
import kebabCase from 'lodash/kebabCase';
import { type Component, defineAsyncComponent, h } from 'vue';
import {
  type GetVNode,
  type GetVNodeFunction,
  type WithRawProps,
} from '@/composition/semantic-html/use-semantic-html-to-vue';

const createVNodeFunction =
  (component: Component): GetVNode =>
  (props, slots) =>
    h(component, { rawProps: props, ...props } satisfies WithRawProps, slots);

const components: Record<string, GetVNode> = {
  feedback: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/feedback/feedback.vue')
    )
  ),
  instructionSlider: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/instruction-slider/instruction-slider.vue')
    )
  ),
  decisionTree: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/decision-tree/index.vue')
    )
  ),
  carousel: createVNodeFunction(
    defineAsyncComponent(() => import('@/components/carousel/carousel.vue'))
  ),
  columnSlider: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/column-slider/column-slider.vue')
    )
  ),
  contextualDictionary: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/contextual-dictionary/contextual-dictionary.vue')
    )
  ),
  expandableContent: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/expandable-content/expandable-content.vue')
    )
  ),
  iydChangeLocation: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/iyd-change-location/iyd-change-location.vue')
    )
  ),
  iydEnterLocation: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/iyd-enter-location/iyd-enter-location.vue')
    )
  ),
  like: createVNodeFunction(
    defineAsyncComponent(() => import('@/components/like/Like.vue'))
  ),
  nestedDollMenu: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/nested-doll-menu/nested-doll-menu.vue')
    )
  ),
  requestRepair: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/request-repair/request-repair.vue')
    )
  ),
  searchFilter: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/search-filter/search-filter.vue')
    )
  ),
  searchNews: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/search-news/search-news.vue')
    )
  ),
  searchResidentsCommittee: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/search-residents-committee/search-residents-committee.vue')
    )
  ),
  tileSlider: createVNodeFunction(
    defineAsyncComponent(
      () => import('@/components/tile-slider/tile-slider.vue')
    )
  ),
  videoPlayer: createVNodeFunction(
    defineAsyncComponent(
      () => import('../../components/video-player/video-player.vue')
    )
  ),
};

const componentList: [string, GetVNode][] = Object.entries(components).map(
  ([name, value]) => [kebabCase(name), value]
);
export const vnodesGetter: GetVNodeFunction = memoize(
  (name?: string): GetVNode | undefined => {
    if (!name) {
      return undefined;
    }
    const kebabName = kebabCase(name);
    const [, component] = componentList.find(([n]) => n === kebabName) || [];
    return component;
  }
);
